import { useAtomValue } from "jotai";
import { tagsAtom, tagIdToKeyValueMapAtom } from "../state/tagAtoms";
import { parseFileTags, serializeFileTags } from "../utils/tag-utils";
import { RemovableTag } from "./RemovableTag";
import { RemovableTagUntagged } from "./RemovableTagUntagged";
import { InlineTagInput } from "./InlineTagInput";

interface TagManagerProps {
    fileTags: string;
    onTagsChange: (newFileTags: string) => void;
    disabled?: boolean;
}

export function TagManager({
    fileTags,
    onTagsChange,
    disabled = false,
}: TagManagerProps) {
    const tags = useAtomValue(tagsAtom);
    const tagIdMap = useAtomValue(tagIdToKeyValueMapAtom);
    const currentTags = parseFileTags(fileTags || "", tagIdMap);

    const handleRemoveTag = (index: number) => {
        const updatedTags = currentTags.filter((_, i) => i !== index);
        const newFileTags = serializeFileTags(updatedTags, tags);
        onTagsChange(newFileTags);
    };

    const handleAddTag = (tag: { key: string; value: string }) => {
        const newTags = [...currentTags, tag].sort((a, b) => {
            if (a.key !== b.key) {
                return a.key.localeCompare(b.key);
            }
            return a.value.localeCompare(b.value);
        });
        const newFileTags = serializeFileTags(newTags, tags);
        onTagsChange(newFileTags);
    };

    return (
        <div className="flex flex-col flex-1 gap-1 items-start">
            {currentTags.length === 0 ? (
                <RemovableTagUntagged />
            ) : (
                currentTags.map((tag, index) => (
                    <RemovableTag
                        key={`${tag.key}:${tag.value}-${index}`}
                        onDelete={() => handleRemoveTag(index)}
                        disabled={disabled}
                    >
                        {tag.key}: {tag.value}
                    </RemovableTag>
                ))
            )}
            {!disabled && (
                <div className="relative z-10 my-1">
                    <InlineTagInput
                        availableTags={tags}
                        currentTags={currentTags}
                        onAddTag={handleAddTag}
                        disabled={disabled}
                    />
                </div>
            )}
        </div>
    );
}
